import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { ApiError } from '../../api/index.ts';
import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { ROUTES } from '../../routing/routes.ts';

export interface ShopLifecycleSectionProps {
  shop: ShopSummary;
  onMutated: () => void;
}

export function ShopLifecycleSection({ shop, onMutated }: Readonly<ShopLifecycleSectionProps>): React.JSX.Element {
  const { api } = useAuth();
  const navigate = useNavigate();
  const archived = shop.archived_at !== null;

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Set the moment a delete 409s with shop_has_reports so the delete dialog
  // can offer a one-click way into the archive flow instead, alongside the
  // server's own message.
  const [showArchiveHint, setShowArchiveHint] = useState(false);

  const confirmDelete = async (): Promise<void> => {
    try {
      await api.shops.remove(shop.id);
      navigate(ROUTES.shops);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'shop_has_reports') {
        setShowArchiveHint(true);
        throw new ApiError(e.status, e.code, `${e.message} Archive the shop instead.`);
      }
      throw e;
    }
  };

  const switchToArchive = () => {
    setDeleteOpen(false);
    setShowArchiveHint(false);
    setArchiveOpen(true);
  };

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h6" gutterBottom>
        Lifecycle
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <QuietButton
          onClick={() => setArchiveOpen(true)}
          sx={archived ? undefined : { color: 'error.main', borderColor: 'error.main' }}
        >
          {archived ? 'Unarchive shop' : 'Archive shop'}
        </QuietButton>
        <QuietButton
          onClick={() => {
            setShowArchiveHint(false);
            setDeleteOpen(true);
          }}
          sx={{ color: 'error.main', borderColor: 'error.main' }}
        >
          Delete shop
        </QuietButton>
      </Box>

      <ConfirmDialog
        open={archiveOpen}
        title={archived ? 'Unarchive shop' : 'Archive shop'}
        body={
          archived
            ? 'Return this shop to the default dashboard and shop lists. Its sync and licence behavior does not change.'
            : 'Hide this shop from the default dashboard and shop lists. It will keep syncing and receiving licences.'
        }
        confirmLabel={archived ? 'Unarchive' : 'Archive'}
        confirmColor="primary"
        onConfirm={async () => {
          if (archived) await api.shops.unarchive(shop.id);
          else await api.shops.archive(shop.id);
        }}
        onClose={() => setArchiveOpen(false)}
        onSuccess={onMutated}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete shop"
        body={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography>
              Permanently delete this shop. This only succeeds if the shop has zero reports and cannot be undone.
            </Typography>
            {showArchiveHint && <QuietButton onClick={switchToArchive}>Archive instead</QuietButton>}
          </Box>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={confirmDelete}
        onClose={() => {
          setDeleteOpen(false);
          setShowArchiveHint(false);
        }}
      />
    </Paper>
  );
}

export default ShopLifecycleSection;
